import { create } from 'zustand';

export type ToastVariant = 'success' | 'error' | 'warning' | 'info';

export interface Toast {
  id: string;
  message: string;
  /** Optional detail line under the message — the "what exactly went wrong" half. */
  description?: string;
  variant: ToastVariant;
  duration?: number;
}

interface ToastStore {
  toasts: Toast[];
  addToast: (message: string, variant?: ToastVariant, duration?: number, description?: string) => void;
  removeToast: (id: string) => void;
}

let toastCounter = 0;

export const useToastStore = create<ToastStore>((set) => ({
  toasts: [],

  addToast: (message, variant = 'success', duration = 4000) => {
    const id = `toast-${++toastCounter}`;
    set((state) => ({
      toasts: [...state.toasts, { id, message, variant, duration }],
    }));

    // Auto-remove
    if (duration > 0) {
      setTimeout(() => {
        set((state) => ({
          toasts: state.toasts.filter((t) => t.id !== id),
        }));
      }, duration);
    }
  },

  removeToast: (id) => {
    set((state) => ({
      toasts: state.toasts.filter((t) => t.id !== id),
    }));
  },
}));

// ─── Convenience functions (importable without hooks) ───
/**
 * `description` is the detail line beneath the message.
 *
 * Callers across the app were already passing it as a second argument — the helpers simply had no
 * parameter for it, so TypeScript flagged the call and the text was silently dropped at runtime.
 * Accepting it here restores the detail those call sites always meant to show.
 */
export const toast = {
  success: (message: string, description?: string) =>
    useToastStore.getState().addToast(message, 'success', 4000, description),
  error: (message: string, description?: string) =>
    useToastStore.getState().addToast(message, 'error', 6000, description),
  warning: (message: string, description?: string) =>
    useToastStore.getState().addToast(message, 'warning', 4000, description),
  info: (message: string, description?: string) =>
    useToastStore.getState().addToast(message, 'info', 4000, description),
};
